import React from 'react';
import { X } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';

export interface CadastroModalProps {
  open: boolean;
  title: string;
  description?: string;
  submitLabel: string;
  submitVariant?: 'primary' | 'danger';
  cancelLabel?: string;
  isSubmitting?: boolean;
  submitError?: string | null;
  canSubmit?: boolean;
  /** `max-w-lg` (padrão) ou `max-w-2xl` para formulários mais largos. */
  size?: 'md' | 'lg';
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}

/**
 * Shell dos diálogos dos cadastros organizacionais — mesmo chrome do
 * `RecrutamentoModal` (cabeçalho com fechar, corpo rolável, rodapé com
 * Cancelar/Confirmar, ESC fechando). Segue o padrão de um shell por
 * feature do repositório.
 */
export function CadastroModal({
  open,
  title,
  description,
  submitLabel,
  submitVariant = 'primary',
  cancelLabel = 'Cancelar',
  isSubmitting,
  submitError,
  canSubmit = true,
  size = 'md',
  onClose,
  onSubmit,
  children,
}: CadastroModalProps) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cadastro-modal-title"
      onClick={onClose}
    >
      <div
        className={`w-full ${size === 'lg' ? 'max-w-2xl' : 'max-w-lg'} rounded-lg bg-white shadow-xl`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
          <div>
            <h2 id="cadastro-modal-title" className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
            {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          {submitError && <Alert variant="error">{submitError}</Alert>}
          {children}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {cancelLabel}
          </Button>
          <Button
            variant={submitVariant}
            loading={isSubmitting}
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
